import { createSelector } from '@reduxjs/toolkit';
import { TStoreState } from 'redux/types';

export const selectShopifyStores = (state: TStoreState) => state.shopifyStores;

const EMPTY_STORES: never[] = [];
const EMPTY_SALES_CHANNELS: never[] = [];
const EMPTY_AVAILABLE_CHANNELS: never[] = [];

export const selectStoresByClientId = (state: TStoreState) =>
	selectShopifyStores(state).storesByClientId;

export const selectStoresByClientIdLoading = (state: TStoreState) =>
	selectShopifyStores(state).storesByClientIdLoading;

export const selectStoresByClientIdList = createSelector(
	[selectStoresByClientId],
	(result) => result?.stores ?? EMPTY_STORES,
);

export const selectStoreSettings = (state: TStoreState) => selectShopifyStores(state).storeSettings;

export const selectStoreSettingsLoading = (state: TStoreState) =>
	selectShopifyStores(state).storeSettingsLoading;

export const selectUpdateStoreSettings = (state: TStoreState) =>
	selectShopifyStores(state).updateStoreSettings;

export const selectUpdateStoreSettingsLoading = (state: TStoreState) =>
	selectShopifyStores(state).updateStoreSettingsLoading;

export const selectUpdateStoreSettingsSuccess = (state: TStoreState) =>
	selectShopifyStores(state).updateStoreSettingsSuccess;

export const selectStoreSalesChannels = (state: TStoreState) =>
	selectShopifyStores(state).storeSalesChannels;

export const selectStoreSalesChannelsLoading = (state: TStoreState) =>
	selectShopifyStores(state).storeSalesChannelsLoading;

export const selectStoreSalesChannelsAvailable = createSelector(
	[selectStoreSalesChannels],
	(result) => result?.available_channels?.data ?? EMPTY_AVAILABLE_CHANNELS,
);

export const selectStoreSalesChannelsPagination = (state: TStoreState) => {
	const data = selectStoreSalesChannels(state);
	return data?.available_channels?.pagination ?? null;
};

export const selectUpdateStoreSalesChannels = (state: TStoreState) =>
	selectShopifyStores(state).updateStoreSalesChannels;

export const selectUpdateStoreSalesChannelsLoading = (state: TStoreState) =>
	selectShopifyStores(state).updateStoreSalesChannelsLoading;

export const selectUpdateStoreSalesChannelsSuccess = (state: TStoreState) =>
	selectShopifyStores(state).updateStoreSalesChannelsSuccess;

export const selectSyncStoreResult = (state: TStoreState) =>
	selectShopifyStores(state).syncStoreResult;

export const selectSyncStoreLoading = (state: TStoreState) =>
	selectShopifyStores(state).syncStoreLoading;

export const selectSyncStoreSuccess = (state: TStoreState) =>
	selectShopifyStores(state).syncStoreSuccess;

export const selectSalesChannelsCatalog = (state: TStoreState) =>
	selectShopifyStores(state).salesChannelsCatalog;

export const selectSalesChannelsCatalogLoading = (state: TStoreState) =>
	selectShopifyStores(state).salesChannelsCatalogLoading;

export const selectSalesChannelsCatalogData = createSelector(
	[selectSalesChannelsCatalog],
	(result) => result?.data ?? EMPTY_SALES_CHANNELS,
);

export const selectSalesChannelsCatalogPagination = (state: TStoreState) => {
	const data = selectSalesChannelsCatalog(state);
	if (!data) return null;

	return {
		pageNumber: data.pageNumber,
		pageSize: data.pageSize,
		totalCount: data.totalCount,
	};
};

export const selectSalesChannelsCatalogTotalCount = (state: TStoreState) =>
	selectSalesChannelsCatalog(state)?.totalCount ?? 0;

export const selectSalesChannel = (state: TStoreState) => selectShopifyStores(state).salesChannel;

export const selectSalesChannelLoading = (state: TStoreState) =>
	selectShopifyStores(state).salesChannelLoading;

export const selectCreateSalesChannelLoading = (state: TStoreState) =>
	selectShopifyStores(state).createSalesChannelLoading;

export const selectCreateSalesChannelSuccess = (state: TStoreState) =>
	selectShopifyStores(state).createSalesChannelSuccess;

export const selectUpdateSalesChannelLoading = (state: TStoreState) =>
	selectShopifyStores(state).updateSalesChannelLoading;

export const selectUpdateSalesChannelSuccess = (state: TStoreState) =>
	selectShopifyStores(state).updateSalesChannelSuccess;

export const selectDeleteSalesChannelLoading = (state: TStoreState) =>
	selectShopifyStores(state).deleteSalesChannelLoading;

export const selectDeleteSalesChannelSuccess = (state: TStoreState) =>
	selectShopifyStores(state).deleteSalesChannelSuccess;
